/**
 * RequirePermissionRoute — per-route RBAC gate (STORY-008-006, Task 4). The
 * finer companion to RequireAdminAuth: that admits any-admin-permission at the
 * shell boundary; this requires a SPECIFIC slug for one page. Missing slug →
 * a Sonner toast naming the slug + redirect to /admin. Server RBAC is the real
 * enforcement; this is UX. Render under <AdminLayout> (RequireAdminAuth already
 * established admin entry), so an authorized admin lacking THIS slug is bounced
 * to the admin landing, not logged out.
 *
 * Reuses the shared `/me` query (`useMe`, cached under ["me"]) — no divergent
 * refetch. By the time a per-route gate mounts inside the shell that query is
 * already resolved, so the loading branch is a defensive edge (RequireAdminAuth
 * owns the real first-load spinner); here it is a minimal status placeholder.
 */
import { type ReactElement, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { registerAdminI18n, ADMIN_NS } from "@/features/admin/api";
import { useMe } from "@/features/settings/api";

export function RequirePermissionRoute({
  slug,
  children,
}: {
  slug: string;
  children: ReactNode;
}): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data: profile, isLoading } = useMe();

  if (isLoading || profile === undefined) {
    return <div role="status" aria-busy="true" data-testid="permission-route-loading" />;
  }
  const permissions: string[] = profile.permissions ?? [];
  if (!permissions.includes(slug)) {
    toast.error(t("permission_denied", { slug }));
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
}

export default RequirePermissionRoute;
